import { Button as UiButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Check, Pencil, X } from 'lucide-react'
import { useState } from 'react'

interface EditableTitleProps {
    title?: string
    canEdit: boolean
    saving?: boolean
    onSave: (title: string) => Promise<boolean>
}

/** Título do processo com edição inline (Enter salva, Esc cancela). */
export const EditableTitle = ({ title = '', canEdit, saving, onSave }: EditableTitleProps) => {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(title)
    const [error, setError] = useState<string | null>(null)

    const start = () => { setDraft(title); setError(null); setEditing(true) }
    const cancel = () => { setEditing(false); setError(null) }
    const save = async () => {
        const value = draft.trim()
        if (value.length < 3) { setError('Informe um título com pelo menos 3 caracteres'); return }
        if (value === title) { setEditing(false); return }
        if (await onSave(value)) setEditing(false)
    }

    const heading = 'mt-1 break-words font-display text-2xl font-normal leading-tight tracking-[-0.02em] text-[#404040] dark:text-foreground md:text-3xl'

    if (!editing) {
        return (
            <div className="group flex items-start gap-2">
                <h1 className={heading}>{title}</h1>
                {canEdit && (
                    <UiButton type="button" size="sm" variant="ghost" className="mt-1 h-8 w-8 p-0 text-muted-foreground" aria-label="Editar título" title="Editar título" onClick={start}>
                        <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </UiButton>
                )}
            </div>
        )
    }

    return (
        <div className="mt-1">
            <div className="flex items-center gap-2">
                <Input
                    aria-label="Título do processo"
                    autoFocus
                    maxLength={200}
                    className="h-10 min-w-0 flex-1 text-lg md:w-[480px] md:flex-none"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); save() }
                        if (e.key === 'Escape') cancel()
                    }}
                />
                <UiButton type="button" size="sm" className="h-10 w-10 p-0" aria-label="Salvar título" disabled={saving} onClick={save}>
                    <Check className="h-4 w-4" strokeWidth={2} />
                </UiButton>
                <UiButton type="button" size="sm" variant="ghost" className="h-10 w-10 p-0" aria-label="Cancelar edição" disabled={saving} onClick={cancel}>
                    <X className="h-4 w-4" strokeWidth={2} />
                </UiButton>
            </div>
            {error && <p className="mt-1 text-xs text-od-secondary-600" role="alert">{error}</p>}
        </div>
    )
}
